// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Nine routes for two kinds of person: the office plans on /production and
// writes its recipes under /production/boms; the bench works on /workshop,
// which is guarded by `production:execute` and never shows a price. The detail
// views are pages and not modals, because an order is a thing people link each
// other to and keep open on a terminal all day.

export const routes = [
  {
    path: '/production',
    name: 'production-orders',
    component: () => import('./pages/OrdersPage.vue'),
    meta: { permission: 'production:read', title: 'production.orders.title' },
  },
  // The static paths win over /:id by route ranking, so the order here does
  // not matter — but they sit first anyway, for the reader.
  {
    path: '/production/new',
    name: 'production-order-new',
    component: () => import('./pages/OrderFormPage.vue'),
    meta: { permission: 'production:write', title: 'production.orders.newTitle' },
  },
  {
    path: '/production/boms',
    name: 'production-boms',
    component: () => import('./pages/BomsPage.vue'),
    meta: { permission: 'production:read', title: 'production.boms.title' },
  },
  {
    path: '/production/boms/new',
    name: 'production-bom-new',
    component: () => import('./pages/BomFormPage.vue'),
    meta: { permission: 'production:write', title: 'production.boms.newTitle' },
  },
  {
    path: '/production/boms/:id/edit',
    name: 'production-bom-edit',
    component: () => import('./pages/BomFormPage.vue'),
    meta: { permission: 'production:write', title: 'production.boms.editTitle' },
  },
  {
    path: '/production/:id',
    name: 'production-order',
    component: () => import('./pages/OrderPage.vue'),
    meta: { permission: 'production:read', title: 'production.orders.detailTitle' },
  },
  {
    path: '/production/:id/edit',
    name: 'production-order-edit',
    component: () => import('./pages/OrderFormPage.vue'),
    meta: { permission: 'production:write', title: 'production.orders.editTitle' },
  },

  // ── The bench ──────────────────────────────────────────────────────────────
  // Behind `production:execute`, not `production:read`: this is the screen
  // everybody in the shop uses, and it shows no prices (backend
  // ReadOrders.floorView).
  {
    path: '/workshop',
    name: 'production-workshop',
    component: () => import('./pages/WorkshopPage.vue'),
    meta: { permission: 'production:execute', title: 'production.workshop.title' },
  },
  {
    path: '/workshop/:id',
    name: 'production-workshop-order',
    component: () => import('./pages/WorkshopOrderPage.vue'),
    meta: { permission: 'production:execute', title: 'production.workshop.title' },
  },
]

/**
 * One row that opens, and not three — same reasoning as inventory.
 *
 * The parent points at the BENCH and is guarded by `production:execute`,
 * because that is the screen both kinds of person can open: `execute` is
 * granted to employee and manager alike at install (module.yaml), while the
 * board and the recipes need `production:read`. Pointing the section at the
 * board would give the shop floor a header that 403s when clicked.
 */
export const navigation = [
  {
    to: '/workshop',
    labelKey: 'production.title',
    permission: 'production:execute',
    icon: 'factory',
    group: 'business',
    children: [
      { to: '/workshop', labelKey: 'production.workshop.title', permission: 'production:execute' },
      { to: '/production', labelKey: 'production.orders.title', permission: 'production:read' },
      { to: '/production/boms', labelKey: 'production.boms.title', permission: 'production:read' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client. That includes what this module is called on the
// PLATFORM's own screens — Roles, Settings, Audit and the assistant panel —
// because a module that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    production: {
      title: 'Producción',
      dashboard: {
        open: 'Órdenes abiertas',
        openHelp: 'Cuántas órdenes de fabricación están pendientes, en curso o en espera.',
        late: 'Órdenes con retraso',
        lateHelp: 'Cuántas órdenes abiertas han pasado de su fecha de fin prevista.',
        pendingHours: 'Horas por fabricar',
        pendingHoursHelp: 'Horas estimadas que quedan en las órdenes abiertas, descontando lo ya trabajado.',
        lateList: 'Las que van tarde',
        lateListHelp: 'Las órdenes abiertas que más se han pasado de fecha. Estira la tarjeta y caben más.',
      },
      subtitle: 'Qué se está fabricando ahora, y con qué material',
      orders: {
        title: 'Órdenes de fabricación',
        subtitle: 'Lo que el taller tiene entre manos: fases, plazos y material',
        detailTitle: 'Orden de fabricación',
        newTitle: 'Nueva orden',
        editTitle: 'Editar orden',
      },
      workshop: {
        title: 'Taller',
        subtitle: 'Tus piezas: toca una para marcar fases y sacar material',
        orderTitle: 'Orden',
        empty: 'Nada en tu banco',
        emptyHint: 'Cuando la oficina te asigne una orden, aparecerá aquí.',
      },
      boms: {
        restored: 'Escandallo devuelto a las listas',
        title: 'Escandallos',
        subtitle: 'Las recetas: qué lleva cada producto y cuánto taller pide',
        newTitle: 'Nuevo escandallo',
        editTitle: 'Editar escandallo',
      },
      status: {
        pending: 'Pendiente', in_progress: 'En fabricación', on_hold: 'Parada',
        completed: 'Terminada', cancelled: 'Anulada',
      },
      priority: { low: 'Baja', medium: 'Normal', high: 'Alta', critical: 'Crítica' },
      actions: {
        new: 'Nueva orden', newBom: 'Nuevo escandallo', edit: 'Editar',
        save: 'Guardar', cancel: 'Cancelar',
        start: 'Iniciar', hold: 'Pausar', resume: 'Reanudar', complete: 'Completar',
        cancelOrder: 'Anular', reopen: 'Reabrir', archive: 'Archivar', restore: 'Devolver a las listas',
        print: 'Imprimir',
        remove: 'Quitar', undo: 'Deshacer',
      },
      // Las palabras de la hoja de ruta. Viajan CON la petición, ya
      // traducidas, para que el papel y la pantalla digan lo mismo (§16).
      pdf: {
        order: 'Orden de fabricación',
        due: 'Para el',
        started: 'Iniciada',
        project: 'Obra',
        recipe: 'Escandallo',
        assignedTo: 'Asignada a',
        taxId: 'NIF',
        phases: 'Fases',
        phase: 'Fase',
        done: 'Hecha',
        who: 'Quién',
        when: 'Cuándo',
        minutes: 'Min.',
        materials: 'Materiales',
        ref: 'Ref.',
        description: 'Descripción',
        planned: 'Previsto',
        taken: 'Consumido',
        pending: 'Falta',
        notes: 'Notas',
        signature: 'Revisado por',
      },
      filters: {
        search: 'Buscar', searchPlaceholder: 'Código, orden, proyecto…',
        bomSearchPlaceholder: 'Código, nombre, categoría…',
        status: 'Estado', allStatuses: 'Todos',
        priority: 'Prioridad', allPriorities: 'Todas',
        onlyOpen: 'Solo abiertas', onlyLate: 'Solo con retraso',
      },
      table: {
        code: 'Código', name: 'Orden', quantity: 'Cantidad', project: 'Proyecto',
        phases: 'Fases', due: 'Entrega prevista', status: 'Estado',
        category: 'Categoría', components: 'Componentes', labour: 'Mano de obra',
        materialCost: 'Coste material',
      },
      kpi: {
        open: 'Órdenes abiertas', late: 'Con retraso',
        estimated: 'Minutos estimados', worked: 'Minutos trabajados',
      },
      empty: {
        none: 'Todavía no hay órdenes',
        noneHint: 'Abre la primera: qué se fabrica, cuántas y por qué fases pasa.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
        boms: 'Todavía no hay escandallos',
        bomsHint: 'Escribe el primero: qué lleva el producto y cuánto taller pide cada unidad.',
      },
      lateBy: '{days} días de retraso',
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      tabs: {
        overview: 'Resumen', phases: 'Fases', material: 'Material', history: 'Historial',
      },
      file: { title: 'Ficha' },
      fields: {
        code: 'Código', name: 'Nombre', description: 'Descripción',
        quantity: 'Cantidad', unit: 'Unidad', producedQuantity: 'Cantidad producida',
        bom: 'Escandallo', noBom: 'Sin escandallo',
        project: 'Proyecto', assignedTo: 'Responsable', team: 'Quién más trabaja en ella',
        priority: 'Prioridad', plannedStart: 'Inicio previsto', plannedEnd: 'Entrega prevista',
        startedAt: 'Iniciada', completedAt: 'Terminada',
        notes: 'Notas', internalNotes: 'Notas internas',
        category: 'Categoría', reason: 'Motivo',
      },
      phases: {
        title: 'Fases de taller',
        progress: '{done} de {total}',
        pending: 'Pendiente',
        minutesLabel: 'Minutos empleados',
        minutesHelp: 'Opcional. Es lo que afina la próxima estimación.',
        noteLabel: 'Nota',
        notePlaceholder: 'Algo que deba saber el siguiente…',
        mark: 'Marcar hecha',
        undoTitle: 'Desmarcar la fase',
        undoBody: 'Se desmarcará «{phase}» y sus minutos se borrarán. Puede volver a marcarse.',
      },
      material: {
        title: 'Material',
        plan: 'Plan de material',
        planned: 'Previsto', consumed: 'Consumido', remaining: 'Pendiente',
        offPlan: 'Fuera de escandallo',
        consumptions: 'Consumos',
        consume: 'Sacar material',
        unitCost: 'Precio', amount: 'Importe',
        none: 'Sin plan de material',
        noneHint: 'Esta orden se abrió sin escandallo. Los consumos se apuntan igual.',
        empty: 'Sin consumos todavía',
        emptyHint: 'Cuando el taller saque material, aparecerá aquí línea a línea.',
      },
      money: {
        title: 'Económica',
        estimated: 'Material previsto', actual: 'Material consumido',
        unpriced: '{count} componentes sin precio',
      },
      history: {
        title: 'Historial',
        notePlaceholder: 'Escribe una nota en la orden…',
        add: 'Anotar',
        phase: 'Fase hecha: {phase}',
        phaseUndone: 'Fase desmarcada: {phase}',
        status: 'Estado: {from} → {to}',
      },
      confirm: {
        completeTitle: 'Completar la orden',
        completeBody: 'Las horas y el material consumido quedan como están y se apuntan al proyecto. Se puede reabrir.',
        producedHelp: 'Las que de verdad han salido buenas. Puede no coincidir con lo pedido.',
        cancelTitle: 'Anular la orden',
        cancelBody: 'El motivo queda en el historial y lo oyen los demás módulos.',
        archiveTitle: 'Archivar la orden',
        archiveBody: 'Desaparece de las listas. Sus números ya están apuntados donde tocaba.',
        revertTitle: 'Deshacer el consumo',
        revertBody: 'Se quitará el consumo de {quantity} de «{name}» y el almacén recuperará el material.',
        archiveBomTitle: 'Archivar el escandallo',
        archiveBomBody: 'Dejará de ofrecerse al abrir órdenes. Las órdenes ya abiertas no se mueven.',
      },
      form: {
        what: 'La pieza', who: 'Quién la hace', when: 'Las fechas', notes: 'Notas',
        namePlaceholder: 'Ventana corredera 120×90…',
        projectPlaceholder: 'A qué trabajo pertenece',
        assignedPlaceholder: 'Quién responde por ella',
        teamPlaceholder: 'Añadir a alguien más…',
        addMember: 'Añadir persona',
        phasesTitle: 'Las fases de esta orden',
        phasesHelp: 'Precargadas con las de vuestro taller. Cambiadlas solo para esta orden: un prototipo puede llevar un paso que ninguna otra lleva.',
        addPhase: 'Añadir fase',
        frozenHint: 'El escandallo, la cantidad y las fases se congelaron al abrir la orden: lo que hay en el banco no se mueve por editar la ficha.',
        internalHelp: 'Solo las ve la oficina. El taller no las recibe.',
        bomSummary: '{count} componentes · {minutes} de taller por {unit}',
        nameRequired: 'La orden necesita un nombre',
      },
      consume: {
        title: 'Sacar material',
        fromPlan: 'Del plan', fromCatalog: 'Del catálogo', freeLine: 'Línea libre',
        searchPlaceholder: 'Buscar material…',
        name: 'Material', namePlaceholder: 'Tornillo 4,5×40…',
        unit: 'Unidad', quantity: 'Cantidad',
        note: 'Nota', notePlaceholder: 'Pieza repetida por golpe…',
        confirm: 'Apuntar',
        remaining: 'quedan {qty}',
        noResults: 'Nada con ese nombre',
      },
      bomForm: {
        header: 'El producto',
        namePlaceholder: 'Ventana corredera 2 hojas…',
        categoryPlaceholder: 'Carpintería, vidrio…',
        unitProduced: 'Unidad producida',
        unitHelp: 'En qué se cuenta lo fabricado: unidades, metros, conjuntos.',
        componentsTitle: 'Componentes',
        addFromCatalog: 'Añadir del catálogo',
        addFree: 'Línea libre',
        componentName: 'Componente',
        componentNotes: 'Notas',
        scrap: 'Merma %',
        freeNamePlaceholder: 'Silicona neutra…',
        noComponents: 'Sin componentes todavía',
        noComponentsHint: 'Busca en el catálogo o añade una línea libre.',
        labourTitle: 'Mano de obra',
        labourDescription: 'Trabajo',
        minutesPerUnit: 'Min. por unidad',
        addLabour: 'Añadir trabajo',
        summary: 'Por unidad producida',
        materialCostPerUnit: 'Coste material',
        unpricedLabel: 'Componentes sin precio',
        minutesPerUnitTotal: 'Minutos de taller',
        nameRequired: 'El escandallo necesita un nombre',
      },
      createdToast: 'Orden {code} abierta',
      savedToast: 'Orden guardada',
      startedToast: 'Orden en fabricación',
      holdToast: 'Orden parada',
      resumedToast: 'Orden reanudada',
      completedToast: 'Orden terminada',
      cancelledToast: 'Orden anulada',
      reopenedToast: 'Orden reabierta',
      archivedToast: 'Orden archivada',
      restoredToast: 'Orden devuelta a las listas',
      archivedBanner: 'Esta orden está archivada: no sale en ninguna lista. Devuélvela para volver a trabajar con ella.',
      revertedToast: 'Consumo deshecho',
      phaseDoneToast: '«{phase}» marcada',
      phaseUndoneToast: '«{phase}» desmarcada',
      consumedToast: 'Consumo apuntado',
      bomCreatedToast: 'Escandallo {code} creado',
      bomSavedToast: 'Escandallo guardado',
      bomArchivedToast: 'Escandallo archivado',
      search: { subtitle: 'Abrir la orden' },
      notifications: {
        categories: { assigned: 'Producción: orden asignada' },
        assigned: {
          title: 'Orden de fabricación asignada',
          body: 'Te han asignado {code} · {name}',
        },
      },
    },
    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan de producción son de este módulo. Van aquí para que instalarlo
    // deje esas tres pantallas completas sin tocar la plataforma.
    modules: { production: 'Producción' },
    assistant: {
      openers: {
        production: {
          'whats-in-production': '¿Qué se está fabricando ahora?',
          'my-bench': '¿Qué tengo que fabricar hoy?',
          'late-orders': '¿Qué órdenes van con retraso en el taller?',
        },
      },
    },
    permissions: {
      production: {
        read: 'Ver las órdenes y los escandallos: fases, cantidades, plan y consumo',
        write: 'Abrir órdenes, editarlas, moverlas de estado y escribir escandallos',
        execute: 'El banco: mis órdenes, marcar fases, apuntar tiempo y sacar material',
        costs: 'Ver lo que cuesta un escandallo y lo que ha costado una orden',
      },
    },
    settings: {
      ns: {
        production: {
          title: 'Producción',
          subtitle: 'Cómo trabaja vuestro taller una orden',
          phases: {
            label: 'Las fases de taller de una orden, en orden',
            help: 'Vuestras, no nuestras: renombrad, añadid, quitad. Las órdenes ya abiertas conservan las fases con las que nacieron.',
          },
          scrapPctDefault: {
            label: 'Merma supuesta en una línea nueva de escandallo',
            help: 'El desperdicio de corte y manipulación sobre la cantidad teórica. Cada línea puede decir la suya; esto es solo con qué empieza una nueva.',
          },
          autoStartOnPhase: {
            label: 'Marcar la primera fase arranca la orden',
            help: 'Activado, el banco arranca una orden trabajándola. Desactivado, alguien con permiso de escritura debe arrancarla antes desde el tablero.',
          },
        },
      },
    },
    audit: {
      actions: {
        production: {
          order_created: 'Orden de fabricación abierta',
          order_updated: 'Orden modificada',
          order_pending: 'Orden devuelta a pendiente',
          order_in_progress: 'Orden puesta en fabricación',
          order_on_hold: 'Orden parada',
          order_completed: 'Orden terminada',
          order_cancelled: 'Orden anulada',
          order_archived: 'Orden archivada',
          order_restored: 'Orden devuelta a las listas',
          order_note_added: 'Nota añadida a la orden',
          phase_done: 'Fase de taller marcada',
          phase_undone: 'Fase de taller desmarcada',
          consumption_recorded: 'Consumo de material apuntado',
          consumption_removed: 'Consumo de material deshecho',
          bom_created: 'Escandallo creado',
          bom_updated: 'Escandallo modificado',
          bom_archived: 'Escandallo archivado',
          bom_restored: 'Escandallo devuelto a las listas',
        },
      },
    },
  },

  ca: {
    production: {
      title: 'Producció',
      dashboard: {
        open: 'Ordres obertes',
        openHelp: 'Quantes ordres de fabricació estan pendents, en curs o en espera.',
        late: 'Ordres amb retard',
        lateHelp: 'Quantes ordres obertes han passat de la data de fi prevista.',
        pendingHours: 'Hores per fabricar',
        pendingHoursHelp: 'Hores estimades que queden a les ordres obertes, descomptant el ja treballat.',
        lateList: 'Les que van tard',
        lateListHelp: 'Les ordres obertes que més s\'han passat de data. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'Què s\'està fabricant ara, i amb quin material',
      orders: {
        title: 'Ordres de fabricació',
        subtitle: 'El que el taller té entre mans: fases, terminis i material',
        detailTitle: 'Ordre de fabricació',
        newTitle: 'Nova ordre',
        editTitle: 'Editar ordre',
      },
      workshop: {
        title: 'Taller',
        subtitle: 'Les teves peces: toca\'n una per marcar fases i treure material',
        orderTitle: 'Ordre',
        empty: 'Res al teu banc',
        emptyHint: 'Quan l\'oficina t\'assigni una ordre, apareixerà aquí.',
      },
      boms: {
        restored: 'Escandall retornat a les llistes',
        title: 'Escandalls',
        subtitle: 'Les receptes: què porta cada producte i quant de taller demana',
        newTitle: 'Nou escandall',
        editTitle: 'Editar escandall',
      },
      status: {
        pending: 'Pendent', in_progress: 'En fabricació', on_hold: 'Aturada',
        completed: 'Acabada', cancelled: 'Anul·lada',
      },
      priority: { low: 'Baixa', medium: 'Normal', high: 'Alta', critical: 'Crítica' },
      actions: {
        new: 'Nova ordre', newBom: 'Nou escandall', edit: 'Editar',
        save: 'Desar', cancel: 'Cancel·lar',
        start: 'Iniciar', hold: 'Pausar', resume: 'Reprendre', complete: 'Completar',
        cancelOrder: 'Anul·lar', reopen: 'Reobrir', archive: 'Arxivar', restore: 'Retornar a les llistes',
        print: 'Imprimir',
        remove: 'Treure', undo: 'Desfer',
      },
      // Las palabras de la hoja de ruta. Viajan CON la petición, ya
      // traducidas, para que el papel y la pantalla digan lo mismo (§16).
      pdf: {
        order: 'Ordre de fabricació',
        due: 'Per al',
        started: 'Iniciada',
        project: 'Obra',
        recipe: 'Escandall',
        assignedTo: 'Assignada a',
        taxId: 'NIF',
        phases: 'Fases',
        phase: 'Fase',
        done: 'Feta',
        who: 'Qui',
        when: 'Quan',
        minutes: 'Min.',
        materials: 'Materials',
        ref: 'Ref.',
        description: 'Descripció',
        planned: 'Previst',
        taken: 'Consumit',
        pending: 'Falta',
        notes: 'Notes',
        signature: 'Revisat per',
      },
      filters: {
        search: 'Cercar', searchPlaceholder: 'Codi, ordre, projecte…',
        bomSearchPlaceholder: 'Codi, nom, categoria…',
        status: 'Estat', allStatuses: 'Tots',
        priority: 'Prioritat', allPriorities: 'Totes',
        onlyOpen: 'Només obertes', onlyLate: 'Només amb retard',
      },
      table: {
        code: 'Codi', name: 'Ordre', quantity: 'Quantitat', project: 'Projecte',
        phases: 'Fases', due: 'Lliurament previst', status: 'Estat',
        category: 'Categoria', components: 'Components', labour: 'Mà d\'obra',
        materialCost: 'Cost material',
      },
      kpi: {
        open: 'Ordres obertes', late: 'Amb retard',
        estimated: 'Minuts estimats', worked: 'Minuts treballats',
      },
      empty: {
        none: 'Encara no hi ha ordres',
        noneHint: 'Obre la primera: què es fabrica, quantes i per quines fases passa.',
        filtered: 'Sense resultats',
        filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
        boms: 'Encara no hi ha escandalls',
        bomsHint: 'Escriu el primer: què porta el producte i quant de taller demana cada unitat.',
      },
      lateBy: '{days} dies de retard',
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      tabs: {
        overview: 'Resum', phases: 'Fases', material: 'Material', history: 'Historial',
      },
      file: { title: 'Fitxa' },
      fields: {
        code: 'Codi', name: 'Nom', description: 'Descripció',
        quantity: 'Quantitat', unit: 'Unitat', producedQuantity: 'Quantitat produïda',
        bom: 'Escandall', noBom: 'Sense escandall',
        project: 'Projecte', assignedTo: 'Responsable', team: 'Qui més hi treballa',
        priority: 'Prioritat', plannedStart: 'Inici previst', plannedEnd: 'Lliurament previst',
        startedAt: 'Iniciada', completedAt: 'Acabada',
        notes: 'Notes', internalNotes: 'Notes internes',
        category: 'Categoria', reason: 'Motiu',
      },
      phases: {
        title: 'Fases de taller',
        progress: '{done} de {total}',
        pending: 'Pendent',
        minutesLabel: 'Minuts emprats',
        minutesHelp: 'Opcional. És el que afina la propera estimació.',
        noteLabel: 'Nota',
        notePlaceholder: 'Alguna cosa que hagi de saber el següent…',
        mark: 'Marcar feta',
        undoTitle: 'Desmarcar la fase',
        undoBody: 'Es desmarcarà «{phase}» i els seus minuts s\'esborraran. Es pot tornar a marcar.',
      },
      material: {
        title: 'Material',
        plan: 'Pla de material',
        planned: 'Previst', consumed: 'Consumit', remaining: 'Pendent',
        offPlan: 'Fora d\'escandall',
        consumptions: 'Consums',
        consume: 'Treure material',
        unitCost: 'Preu', amount: 'Import',
        none: 'Sense pla de material',
        noneHint: 'Aquesta ordre es va obrir sense escandall. Els consums s\'apunten igual.',
        empty: 'Sense consums encara',
        emptyHint: 'Quan el taller tregui material, apareixerà aquí línia a línia.',
      },
      money: {
        title: 'Econòmica',
        estimated: 'Material previst', actual: 'Material consumit',
        unpriced: '{count} components sense preu',
      },
      history: {
        title: 'Historial',
        notePlaceholder: 'Escriu una nota a l\'ordre…',
        add: 'Anotar',
        phase: 'Fase feta: {phase}',
        phaseUndone: 'Fase desmarcada: {phase}',
        status: 'Estat: {from} → {to}',
      },
      confirm: {
        completeTitle: 'Completar l\'ordre',
        completeBody: 'Les hores i el material consumit queden com estan i s\'apunten al projecte. Es pot reobrir.',
        producedHelp: 'Les que de debò han sortit bones. Pot no coincidir amb el que es va demanar.',
        cancelTitle: 'Anul·lar l\'ordre',
        cancelBody: 'El motiu queda a l\'historial i el senten els altres mòduls.',
        archiveTitle: 'Arxivar l\'ordre',
        archiveBody: 'Desapareix de les llistes. Els seus números ja estan apuntats on tocava.',
        revertTitle: 'Desfer el consum',
        revertBody: 'Es traurà el consum de {quantity} de «{name}» i el magatzem recuperarà el material.',
        archiveBomTitle: 'Arxivar l\'escandall',
        archiveBomBody: 'Deixarà d\'oferir-se en obrir ordres. Les ordres ja obertes no es mouen.',
      },
      form: {
        what: 'La peça', who: 'Qui la fa', when: 'Les dates', notes: 'Notes',
        namePlaceholder: 'Finestra corredissa 120×90…',
        projectPlaceholder: 'A quina feina pertany',
        assignedPlaceholder: 'Qui en respon',
        teamPlaceholder: 'Afegir algú més…',
        addMember: 'Afegir persona',
        phasesTitle: 'Les fases d\'aquesta ordre',
        phasesHelp: 'Precarregades amb les del vostre taller. Canvieu-les només per a aquesta ordre: un prototip pot portar un pas que cap altra porta.',
        addPhase: 'Afegir fase',
        frozenHint: 'L\'escandall, la quantitat i les fases es van congelar en obrir l\'ordre: el que hi ha al banc no es mou per editar la fitxa.',
        internalHelp: 'Només les veu l\'oficina. El taller no les rep.',
        bomSummary: '{count} components · {minutes} de taller per {unit}',
        nameRequired: 'L\'ordre necessita un nom',
      },
      consume: {
        title: 'Treure material',
        fromPlan: 'Del pla', fromCatalog: 'Del catàleg', freeLine: 'Línia lliure',
        searchPlaceholder: 'Cercar material…',
        name: 'Material', namePlaceholder: 'Cargol 4,5×40…',
        unit: 'Unitat', quantity: 'Quantitat',
        note: 'Nota', notePlaceholder: 'Peça repetida per un cop…',
        confirm: 'Apuntar',
        remaining: 'en queden {qty}',
        noResults: 'Res amb aquest nom',
      },
      bomForm: {
        header: 'El producte',
        namePlaceholder: 'Finestra corredissa 2 fulles…',
        categoryPlaceholder: 'Fusteria, vidre…',
        unitProduced: 'Unitat produïda',
        unitHelp: 'En què es compta el fabricat: unitats, metres, conjunts.',
        componentsTitle: 'Components',
        addFromCatalog: 'Afegir del catàleg',
        addFree: 'Línia lliure',
        componentName: 'Component',
        componentNotes: 'Notes',
        scrap: 'Minva %',
        freeNamePlaceholder: 'Silicona neutra…',
        noComponents: 'Sense components encara',
        noComponentsHint: 'Cerca al catàleg o afegeix una línia lliure.',
        labourTitle: 'Mà d\'obra',
        labourDescription: 'Feina',
        minutesPerUnit: 'Min. per unitat',
        addLabour: 'Afegir feina',
        summary: 'Per unitat produïda',
        materialCostPerUnit: 'Cost material',
        unpricedLabel: 'Components sense preu',
        minutesPerUnitTotal: 'Minuts de taller',
        nameRequired: 'L\'escandall necessita un nom',
      },
      createdToast: 'Ordre {code} oberta',
      savedToast: 'Ordre desada',
      startedToast: 'Ordre en fabricació',
      holdToast: 'Ordre aturada',
      resumedToast: 'Ordre represa',
      completedToast: 'Ordre acabada',
      cancelledToast: 'Ordre anul·lada',
      reopenedToast: 'Ordre reoberta',
      archivedToast: 'Ordre arxivada',
      restoredToast: 'Ordre retornada a les llistes',
      archivedBanner: 'Aquesta ordre està arxivada: no surt a cap llista. Retorna-la per tornar-hi a treballar.',
      revertedToast: 'Consum desfet',
      phaseDoneToast: '«{phase}» marcada',
      phaseUndoneToast: '«{phase}» desmarcada',
      consumedToast: 'Consum apuntat',
      bomCreatedToast: 'Escandall {code} creat',
      bomSavedToast: 'Escandall desat',
      bomArchivedToast: 'Escandall arxivat',
      search: { subtitle: 'Obrir l\'ordre' },
      notifications: {
        categories: { assigned: 'Producció: ordre assignada' },
        assigned: {
          title: 'Ordre de fabricació assignada',
          body: 'T\'han assignat {code} · {name}',
        },
      },
    },
    modules: { production: 'Producció' },
    assistant: {
      openers: {
        production: {
          'whats-in-production': 'Què s\'està fabricant ara?',
          'my-bench': 'Què he de fabricar avui?',
          'late-orders': 'Quines ordres van amb retard al taller?',
        },
      },
    },
    permissions: {
      production: {
        read: 'Veure les ordres i els escandalls: fases, quantitats, pla i consum',
        write: 'Obrir ordres, editar-les, moure-les d\'estat i escriure escandalls',
        execute: 'El banc: les meves ordres, marcar fases, apuntar temps i treure material',
        costs: 'Veure què costa un escandall i què ha costat una ordre',
      },
    },
    settings: {
      ns: {
        production: {
          title: 'Producció',
          subtitle: 'Com treballa el vostre taller una ordre',
          phases: {
            label: 'Les fases de taller d\'una ordre, en ordre',
            help: 'Vostres, no nostres: reanomeneu, afegiu, traieu. Les ordres ja obertes conserven les fases amb què van néixer.',
          },
          scrapPctDefault: {
            label: 'Minva suposada en una línia nova d\'escandall',
            help: 'El malbaratament de tall i manipulació sobre la quantitat teòrica. Cada línia pot dir la seva; això és només amb què comença una de nova.',
          },
          autoStartOnPhase: {
            label: 'Marcar la primera fase arrenca l\'ordre',
            help: 'Activat, el banc arrenca una ordre treballant-la. Desactivat, algú amb permís d\'escriptura l\'ha d\'arrencar abans des del tauler.',
          },
        },
      },
    },
    audit: {
      actions: {
        production: {
          order_created: 'Ordre de fabricació oberta',
          order_updated: 'Ordre modificada',
          order_pending: 'Ordre tornada a pendent',
          order_in_progress: 'Ordre posada en fabricació',
          order_on_hold: 'Ordre aturada',
          order_completed: 'Ordre acabada',
          order_cancelled: 'Ordre anul·lada',
          order_archived: 'Ordre arxivada',
          order_restored: 'Ordre retornada a les llistes',
          order_note_added: 'Nota afegida a l\'ordre',
          phase_done: 'Fase de taller marcada',
          phase_undone: 'Fase de taller desmarcada',
          consumption_recorded: 'Consum de material apuntat',
          consumption_removed: 'Consum de material desfet',
          bom_created: 'Escandall creat',
          bom_updated: 'Escandall modificat',
          bom_archived: 'Escandall arxivat',
          bom_restored: 'Escandall retornat a les llistes',
        },
      },
    },
  },

  en: {
    production: {
      title: 'Production',
      dashboard: {
        open: 'Open orders',
        openHelp: 'How many production orders are pending, in progress or on hold.',
        late: 'Orders running late',
        lateHelp: 'How many open orders are past their planned end date.',
        pendingHours: 'Hours left to make',
        pendingHoursHelp: 'Estimated hours left on the open orders, minus what has already been worked.',
        lateList: 'The ones running late',
        lateListHelp: 'The open orders furthest past their date. Stretch the card and more fit.',
      },
      subtitle: 'What is being made right now, and with what material',
      orders: {
        title: 'Manufacturing orders',
        subtitle: 'What the shop has in hand: phases, deadlines and material',
        detailTitle: 'Manufacturing order',
        newTitle: 'New order',
        editTitle: 'Edit order',
      },
      workshop: {
        title: 'Workshop',
        subtitle: 'Your pieces: tap one to tick phases and draw material',
        orderTitle: 'Order',
        empty: 'Nothing on your bench',
        emptyHint: 'When the office assigns you an order, it will show up here.',
      },
      boms: {
        restored: 'Bill of materials brought back',
        title: 'Bills of materials',
        subtitle: 'The recipes: what goes into each product and how much shop time it asks',
        newTitle: 'New bill of materials',
        editTitle: 'Edit bill of materials',
      },
      status: {
        pending: 'Pending', in_progress: 'In production', on_hold: 'On hold',
        completed: 'Completed', cancelled: 'Cancelled',
      },
      priority: { low: 'Low', medium: 'Normal', high: 'High', critical: 'Critical' },
      actions: {
        new: 'New order', newBom: 'New bill of materials', edit: 'Edit',
        save: 'Save', cancel: 'Cancel',
        start: 'Start', hold: 'Put on hold', resume: 'Resume', complete: 'Complete',
        cancelOrder: 'Cancel order', reopen: 'Reopen', archive: 'Archive', restore: 'Bring back',
        print: 'Print',
        remove: 'Remove', undo: 'Undo',
      },
      // Las palabras de la hoja de ruta. Viajan CON la petición, ya
      // traducidas, para que el papel y la pantalla digan lo mismo (§16).
      pdf: {
        order: 'Manufacturing order',
        due: 'Due',
        started: 'Started',
        project: 'Job',
        recipe: 'Recipe',
        assignedTo: 'Assigned to',
        taxId: 'Tax id',
        phases: 'Operations',
        phase: 'Operation',
        done: 'Done',
        who: 'Who',
        when: 'When',
        minutes: 'Min.',
        materials: 'Materials',
        ref: 'Ref.',
        description: 'Description',
        planned: 'Planned',
        taken: 'Taken',
        pending: 'Still to take',
        notes: 'Notes',
        signature: 'Checked by',
      },
      filters: {
        search: 'Search', searchPlaceholder: 'Code, order, project…',
        bomSearchPlaceholder: 'Code, name, category…',
        status: 'Status', allStatuses: 'All',
        priority: 'Priority', allPriorities: 'All',
        onlyOpen: 'Only open', onlyLate: 'Only late',
      },
      table: {
        code: 'Code', name: 'Order', quantity: 'Quantity', project: 'Project',
        phases: 'Phases', due: 'Planned delivery', status: 'Status',
        category: 'Category', components: 'Components', labour: 'Labour',
        materialCost: 'Material cost',
      },
      kpi: {
        open: 'Open orders', late: 'Late',
        estimated: 'Estimated minutes', worked: 'Worked minutes',
      },
      empty: {
        none: 'No orders yet',
        noneHint: 'Open the first one: what is being made, how many, through which phases.',
        filtered: 'No results',
        filteredHint: 'Try removing a filter or searching for something else.',
        boms: 'No bills of materials yet',
        bomsHint: 'Write the first one: what goes into the product and how much shop time each unit asks.',
      },
      lateBy: '{days} days late',
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      tabs: {
        overview: 'Overview', phases: 'Phases', material: 'Material', history: 'History',
      },
      file: { title: 'File' },
      fields: {
        code: 'Code', name: 'Name', description: 'Description',
        quantity: 'Quantity', unit: 'Unit', producedQuantity: 'Quantity produced',
        bom: 'Bill of materials', noBom: 'No bill of materials',
        project: 'Project', assignedTo: 'Assignee', team: 'Who else works on it',
        priority: 'Priority', plannedStart: 'Planned start', plannedEnd: 'Planned delivery',
        startedAt: 'Started', completedAt: 'Completed',
        notes: 'Notes', internalNotes: 'Internal notes',
        category: 'Category', reason: 'Reason',
      },
      phases: {
        title: 'Shop phases',
        progress: '{done} of {total}',
        pending: 'Pending',
        minutesLabel: 'Minutes spent',
        minutesHelp: 'Optional. It is what sharpens the next estimate.',
        noteLabel: 'Note',
        notePlaceholder: 'Something the next person should know…',
        mark: 'Mark done',
        undoTitle: 'Untick the phase',
        undoBody: '“{phase}” will be unticked and its minutes wiped. It can be ticked again.',
      },
      material: {
        title: 'Material',
        plan: 'Material plan',
        planned: 'Planned', consumed: 'Consumed', remaining: 'Remaining',
        offPlan: 'Off the bill of materials',
        consumptions: 'Consumptions',
        consume: 'Draw material',
        unitCost: 'Price', amount: 'Amount',
        none: 'No material plan',
        noneHint: 'This order was opened without a bill of materials. Consumptions are logged all the same.',
        empty: 'No consumptions yet',
        emptyHint: 'When the shop draws material, it will show up here line by line.',
      },
      money: {
        title: 'Financials',
        estimated: 'Planned material', actual: 'Consumed material',
        unpriced: '{count} unpriced components',
      },
      history: {
        title: 'History',
        notePlaceholder: 'Write a note on the order…',
        add: 'Add',
        phase: 'Phase done: {phase}',
        phaseUndone: 'Phase unticked: {phase}',
        status: 'Status: {from} → {to}',
      },
      confirm: {
        completeTitle: 'Complete the order',
        completeBody: 'The hours and the material consumed stay as they are and are posted to the project. It can be reopened.',
        producedHelp: 'The ones that really came out good. It may not match what was asked for.',
        cancelTitle: 'Cancel the order',
        cancelBody: 'The reason goes into the history and the other modules hear it.',
        archiveTitle: 'Archive the order',
        archiveBody: 'It disappears from the lists. Its numbers are already posted where they belong.',
        revertTitle: 'Undo the consumption',
        revertBody: 'The {quantity} draw of “{name}” will be removed and the warehouse gets the material back.',
        archiveBomTitle: 'Archive the bill of materials',
        archiveBomBody: 'It stops being offered when opening orders. Orders already open do not move.',
      },
      form: {
        what: 'The piece', who: 'Who makes it', when: 'The dates', notes: 'Notes',
        namePlaceholder: 'Sliding window 120×90…',
        projectPlaceholder: 'Which job it belongs to',
        assignedPlaceholder: 'Who answers for it',
        teamPlaceholder: 'Add somebody else…',
        addMember: 'Add person',
        phasesTitle: 'The phases of this order',
        phasesHelp: 'Preloaded with your shop\'s own. Change them for this order only: a prototype can carry a step no other order has.',
        addPhase: 'Add phase',
        frozenHint: 'The bill of materials, the quantity and the phases were frozen when the order was opened: what is on the bench does not move because the file is edited.',
        internalHelp: 'Only the office sees these. The shop does not receive them.',
        bomSummary: '{count} components · {minutes} of shop time per {unit}',
        nameRequired: 'An order needs a name',
      },
      consume: {
        title: 'Draw material',
        fromPlan: 'From the plan', fromCatalog: 'From the catalogue', freeLine: 'Free line',
        searchPlaceholder: 'Search materials…',
        name: 'Material', namePlaceholder: 'Screw 4.5×40…',
        unit: 'Unit', quantity: 'Quantity',
        note: 'Note', notePlaceholder: 'Piece redone after a knock…',
        confirm: 'Log it',
        remaining: '{qty} left',
        noResults: 'Nothing by that name',
      },
      bomForm: {
        header: 'The product',
        namePlaceholder: 'Sliding window, 2 leaves…',
        categoryPlaceholder: 'Joinery, glass…',
        unitProduced: 'Unit produced',
        unitHelp: 'What the output is counted in: units, metres, sets.',
        componentsTitle: 'Components',
        addFromCatalog: 'Add from the catalogue',
        addFree: 'Free line',
        componentName: 'Component',
        componentNotes: 'Notes',
        scrap: 'Scrap %',
        freeNamePlaceholder: 'Neutral silicone…',
        noComponents: 'No components yet',
        noComponentsHint: 'Search the catalogue or add a free line.',
        labourTitle: 'Labour',
        labourDescription: 'Task',
        minutesPerUnit: 'Min. per unit',
        addLabour: 'Add task',
        summary: 'Per unit produced',
        materialCostPerUnit: 'Material cost',
        unpricedLabel: 'Unpriced components',
        minutesPerUnitTotal: 'Shop minutes',
        nameRequired: 'A bill of materials needs a name',
      },
      createdToast: 'Order {code} opened',
      savedToast: 'Order saved',
      startedToast: 'Order in production',
      holdToast: 'Order on hold',
      resumedToast: 'Order resumed',
      completedToast: 'Order completed',
      cancelledToast: 'Order cancelled',
      reopenedToast: 'Order reopened',
      archivedToast: 'Order archived',
      restoredToast: 'Order brought back',
      archivedBanner: 'This order is archived: it is on no list. Bring it back to work with it again.',
      revertedToast: 'Consumption undone',
      phaseDoneToast: '“{phase}” marked done',
      phaseUndoneToast: '“{phase}” unticked',
      consumedToast: 'Consumption logged',
      bomCreatedToast: 'Bill of materials {code} created',
      bomSavedToast: 'Bill of materials saved',
      bomArchivedToast: 'Bill of materials archived',
      search: { subtitle: 'Open the order' },
      notifications: {
        categories: { assigned: 'Production: order assigned' },
        assigned: {
          title: 'Manufacturing order assigned to you',
          body: 'You have been assigned {code} · {name}',
        },
      },
    },
    modules: { production: 'Production' },
    assistant: {
      openers: {
        production: {
          'whats-in-production': 'What is in production right now?',
          'my-bench': 'What do I have to make today?',
          'late-orders': 'Which orders are late in the shop?',
        },
      },
    },
    permissions: {
      production: {
        read: 'See the orders and the bills of materials: phases, quantities, plan and consumption',
        write: 'Open orders, edit them, move their status and write bills of materials',
        execute: 'The bench: my orders, tick phases, log time and draw material',
        costs: 'See what a bill of materials costs and what an order has really cost',
      },
    },
    settings: {
      ns: {
        production: {
          title: 'Production',
          subtitle: 'How your workshop runs an order',
          phases: {
            label: 'The shop phases an order goes through, in order',
            help: 'Yours, not ours: rename them, add them, remove them. Orders already open keep the phases they were born with.',
          },
          scrapPctDefault: {
            label: 'Scrap assumed on a new recipe line',
            help: 'The cutting and handling waste on top of the theoretical quantity. Each line can say its own; this is only what a new one starts with.',
          },
          autoStartOnPhase: {
            label: 'Ticking the first phase starts the order',
            help: 'On, the bench starts an order by working on it. Off, somebody with the write permission must start it from the board first.',
          },
        },
      },
    },
    audit: {
      actions: {
        production: {
          order_created: 'Manufacturing order opened',
          order_updated: 'Order updated',
          order_pending: 'Order returned to pending',
          order_in_progress: 'Order set in production',
          order_on_hold: 'Order put on hold',
          order_completed: 'Order completed',
          order_cancelled: 'Order cancelled',
          order_archived: 'Order archived',
          order_restored: 'Order brought back',
          order_note_added: 'Note added to the order',
          phase_done: 'Shop phase marked done',
          phase_undone: 'Shop phase unticked',
          consumption_recorded: 'Material consumption logged',
          consumption_removed: 'Material consumption undone',
          bom_created: 'Bill of materials created',
          bom_updated: 'Bill of materials updated',
          bom_archived: 'Bill of materials archived',
          bom_restored: 'Bill of materials brought back',
        },
      },
    },
  },

  fr: {
    production: {
      title: 'Production',
      dashboard: {
        open: 'Ordres ouverts',
        openHelp: 'Combien d\'ordres de fabrication sont en attente, en cours ou suspendus.',
        late: 'Ordres en retard',
        lateHelp: 'Combien d\'ordres ouverts ont dépassé leur date de fin prévue.',
        pendingHours: 'Heures à produire',
        pendingHoursHelp: 'Heures estimées restantes sur les ordres ouverts, déduction faite du travail déjà fait.',
        lateList: 'Ceux qui sont en retard',
        lateListHelp: 'Les ordres ouverts les plus en retard sur leur date. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Ce qui se fabrique en ce moment, et avec quel matériel',
      orders: {
        title: 'Ordres de fabrication',
        subtitle: 'Ce que l\'atelier a en main : phases, délais et matériel',
        detailTitle: 'Ordre de fabrication',
        newTitle: 'Nouvel ordre',
        editTitle: 'Modifier l\'ordre',
      },
      workshop: {
        title: 'Atelier',
        subtitle: 'Vos pièces : touchez-en une pour cocher les phases et sortir du matériel',
        orderTitle: 'Ordre',
        empty: 'Rien sur votre établi',
        emptyHint: 'Quand le bureau vous assignera un ordre, il apparaîtra ici.',
      },
      boms: {
        restored: 'Nomenclature remise dans les listes',
        title: 'Nomenclatures',
        subtitle: 'Les recettes : ce que porte chaque produit et le temps d\'atelier qu\'il demande',
        newTitle: 'Nouvelle nomenclature',
        editTitle: 'Modifier la nomenclature',
      },
      status: {
        pending: 'En attente', in_progress: 'En fabrication', on_hold: 'En pause',
        completed: 'Terminée', cancelled: 'Annulée',
      },
      priority: { low: 'Basse', medium: 'Normale', high: 'Haute', critical: 'Critique' },
      actions: {
        new: 'Nouvel ordre', newBom: 'Nouvelle nomenclature', edit: 'Modifier',
        save: 'Enregistrer', cancel: 'Annuler',
        start: 'Démarrer', hold: 'Mettre en pause', resume: 'Reprendre', complete: 'Terminer',
        cancelOrder: 'Annuler l\'ordre', reopen: 'Rouvrir', archive: 'Archiver', restore: 'Remettre dans les listes',
        print: 'Imprimer',
        remove: 'Retirer', undo: 'Défaire',
      },
      // Las palabras de la hoja de ruta. Viajan CON la petición, ya
      // traducidas, para que el papel y la pantalla digan lo mismo (§16).
      pdf: {
        order: 'Ordre de fabrication',
        due: 'Pour le',
        started: 'Démarré',
        project: 'Chantier',
        recipe: 'Nomenclature',
        assignedTo: 'Assigné à',
        taxId: 'N° TVA',
        phases: 'Opérations',
        phase: 'Opération',
        done: 'Fait',
        who: 'Qui',
        when: 'Quand',
        minutes: 'Min.',
        materials: 'Matériaux',
        ref: 'Réf.',
        description: 'Description',
        planned: 'Prévu',
        taken: 'Consommé',
        pending: 'Reste',
        notes: 'Notes',
        signature: 'Vérifié par',
      },
      filters: {
        search: 'Rechercher', searchPlaceholder: 'Code, ordre, projet…',
        bomSearchPlaceholder: 'Code, nom, catégorie…',
        status: 'État', allStatuses: 'Tous',
        priority: 'Priorité', allPriorities: 'Toutes',
        onlyOpen: 'Seulement ouverts', onlyLate: 'Seulement en retard',
      },
      table: {
        code: 'Code', name: 'Ordre', quantity: 'Quantité', project: 'Projet',
        phases: 'Phases', due: 'Livraison prévue', status: 'État',
        category: 'Catégorie', components: 'Composants', labour: 'Main-d\'œuvre',
        materialCost: 'Coût matière',
      },
      kpi: {
        open: 'Ordres ouverts', late: 'En retard',
        estimated: 'Minutes estimées', worked: 'Minutes travaillées',
      },
      empty: {
        none: 'Pas encore d\'ordres',
        noneHint: 'Ouvrez le premier : ce qui se fabrique, combien, par quelles phases.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essayez de retirer un filtre ou de chercher autre chose.',
        boms: 'Pas encore de nomenclatures',
        bomsHint: 'Écrivez la première : ce que porte le produit et le temps d\'atelier par unité.',
      },
      lateBy: '{days} jours de retard',
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      tabs: {
        overview: 'Résumé', phases: 'Phases', material: 'Matériel', history: 'Historique',
      },
      file: { title: 'Fiche' },
      fields: {
        code: 'Code', name: 'Nom', description: 'Description',
        quantity: 'Quantité', unit: 'Unité', producedQuantity: 'Quantité produite',
        bom: 'Nomenclature', noBom: 'Sans nomenclature',
        project: 'Projet', assignedTo: 'Responsable', team: 'Qui d’autre y travaille',
        priority: 'Priorité', plannedStart: 'Début prévu', plannedEnd: 'Livraison prévue',
        startedAt: 'Démarrée', completedAt: 'Terminée',
        notes: 'Notes', internalNotes: 'Notes internes',
        category: 'Catégorie', reason: 'Motif',
      },
      phases: {
        title: 'Phases d\'atelier',
        progress: '{done} sur {total}',
        pending: 'En attente',
        minutesLabel: 'Minutes passées',
        minutesHelp: 'Facultatif. C\'est ce qui affine la prochaine estimation.',
        noteLabel: 'Note',
        notePlaceholder: 'Quelque chose que le suivant doit savoir…',
        mark: 'Marquer faite',
        undoTitle: 'Décocher la phase',
        undoBody: '« {phase} » sera décochée et ses minutes effacées. Elle peut être recochée.',
      },
      material: {
        title: 'Matériel',
        plan: 'Plan de matière',
        planned: 'Prévu', consumed: 'Consommé', remaining: 'Restant',
        offPlan: 'Hors nomenclature',
        consumptions: 'Consommations',
        consume: 'Sortir du matériel',
        unitCost: 'Prix', amount: 'Montant',
        none: 'Pas de plan de matière',
        noneHint: 'Cet ordre a été ouvert sans nomenclature. Les consommations se notent quand même.',
        empty: 'Pas encore de consommations',
        emptyHint: 'Quand l\'atelier sortira du matériel, il apparaîtra ici ligne à ligne.',
      },
      money: {
        title: 'Économique',
        estimated: 'Matière prévue', actual: 'Matière consommée',
        unpriced: '{count} composants sans prix',
      },
      history: {
        title: 'Historique',
        notePlaceholder: 'Écrire une note sur l\'ordre…',
        add: 'Noter',
        phase: 'Phase faite : {phase}',
        phaseUndone: 'Phase décochée : {phase}',
        status: 'État : {from} → {to}',
      },
      confirm: {
        completeTitle: 'Terminer l\'ordre',
        completeBody: 'Les heures et la matière consommée restent telles quelles et sont portées au projet. Il peut être rouvert.',
        producedHelp: 'Celles qui sont vraiment sorties bonnes. Peut différer de ce qui était demandé.',
        cancelTitle: 'Annuler l\'ordre',
        cancelBody: 'Le motif reste dans l\'historique et les autres modules l\'entendent.',
        archiveTitle: 'Archiver l\'ordre',
        archiveBody: 'Il disparaît des listes. Ses chiffres sont déjà portés là où il faut.',
        revertTitle: 'Défaire la consommation',
        revertBody: 'La sortie de {quantity} de « {name} » sera retirée et le magasin récupérera la matière.',
        archiveBomTitle: 'Archiver la nomenclature',
        archiveBomBody: 'Elle cessera d\'être proposée à l\'ouverture d\'ordres. Les ordres déjà ouverts ne bougent pas.',
      },
      form: {
        what: 'La pièce', who: 'Qui la fait', when: 'Les dates', notes: 'Notes',
        namePlaceholder: 'Fenêtre coulissante 120×90…',
        projectPlaceholder: 'À quel chantier elle appartient',
        assignedPlaceholder: 'Qui en répond',
        teamPlaceholder: 'Ajouter quelqu’un…',
        addMember: 'Ajouter une personne',
        phasesTitle: 'Les phases de cet ordre',
        phasesHelp: 'Préchargées avec celles de votre atelier. Changez-les pour cet ordre seulement : un prototype peut porter une étape qu\'aucun autre ne porte.',
        addPhase: 'Ajouter une phase',
        frozenHint: 'La nomenclature, la quantité et les phases ont été figées à l\'ouverture de l\'ordre : ce qui est sur l\'établi ne bouge pas parce qu\'on modifie la fiche.',
        internalHelp: 'Seul le bureau les voit. L\'atelier ne les reçoit pas.',
        bomSummary: '{count} composants · {minutes} d\'atelier par {unit}',
        nameRequired: 'Un ordre a besoin d\'un nom',
      },
      consume: {
        title: 'Sortir du matériel',
        fromPlan: 'Du plan', fromCatalog: 'Du catalogue', freeLine: 'Ligne libre',
        searchPlaceholder: 'Chercher un matériel…',
        name: 'Matériel', namePlaceholder: 'Vis 4,5×40…',
        unit: 'Unité', quantity: 'Quantité',
        note: 'Note', notePlaceholder: 'Pièce refaite après un choc…',
        confirm: 'Noter',
        remaining: 'reste {qty}',
        noResults: 'Rien à ce nom',
      },
      bomForm: {
        header: 'Le produit',
        namePlaceholder: 'Fenêtre coulissante 2 vantaux…',
        categoryPlaceholder: 'Menuiserie, vitrage…',
        unitProduced: 'Unité produite',
        unitHelp: 'En quoi se compte le produit : unités, mètres, ensembles.',
        componentsTitle: 'Composants',
        addFromCatalog: 'Ajouter du catalogue',
        addFree: 'Ligne libre',
        componentName: 'Composant',
        componentNotes: 'Notes',
        scrap: 'Chute %',
        freeNamePlaceholder: 'Silicone neutre…',
        noComponents: 'Pas encore de composants',
        noComponentsHint: 'Cherchez dans le catalogue ou ajoutez une ligne libre.',
        labourTitle: 'Main-d\'œuvre',
        labourDescription: 'Tâche',
        minutesPerUnit: 'Min. par unité',
        addLabour: 'Ajouter une tâche',
        summary: 'Par unité produite',
        materialCostPerUnit: 'Coût matière',
        unpricedLabel: 'Composants sans prix',
        minutesPerUnitTotal: 'Minutes d\'atelier',
        nameRequired: 'Une nomenclature a besoin d\'un nom',
      },
      createdToast: 'Ordre {code} ouvert',
      savedToast: 'Ordre enregistré',
      startedToast: 'Ordre en fabrication',
      holdToast: 'Ordre en pause',
      resumedToast: 'Ordre repris',
      completedToast: 'Ordre terminé',
      cancelledToast: 'Ordre annulé',
      reopenedToast: 'Ordre rouvert',
      archivedToast: 'Ordre archivé',
      restoredToast: 'Ordre remis dans les listes',
      archivedBanner: 'Cet ordre est archivé : il ne figure sur aucune liste. Remettez-le pour y retravailler.',
      revertedToast: 'Consommation défaite',
      phaseDoneToast: '« {phase} » marquée faite',
      phaseUndoneToast: '« {phase} » décochée',
      consumedToast: 'Consommation notée',
      bomCreatedToast: 'Nomenclature {code} créée',
      bomSavedToast: 'Nomenclature enregistrée',
      bomArchivedToast: 'Nomenclature archivée',
      search: { subtitle: 'Ouvrir l\'ordre' },
      notifications: {
        categories: { assigned: 'Production : ordre assigné' },
        assigned: {
          title: 'Ordre de fabrication assigné',
          body: 'On vous a assigné {code} · {name}',
        },
      },
    },
    modules: { production: 'Production' },
    assistant: {
      openers: {
        production: {
          'whats-in-production': 'Qu\'est-ce qui est en fabrication en ce moment ?',
          'my-bench': 'Qu\'est-ce que je dois fabriquer aujourd\'hui ?',
          'late-orders': 'Quels ordres sont en retard à l\'atelier ?',
        },
      },
    },
    permissions: {
      production: {
        read: 'Voir les ordres et les nomenclatures : phases, quantités, plan et consommation',
        write: 'Ouvrir des ordres, les modifier, changer leur état et écrire des nomenclatures',
        execute: 'L\'établi : mes ordres, cocher des phases, noter le temps et sortir du matériel',
        costs: 'Voir ce que coûte une nomenclature et ce qu\'un ordre a vraiment coûté',
      },
    },
    settings: {
      ns: {
        production: {
          title: 'Production',
          subtitle: 'Comment votre atelier mène un ordre',
          phases: {
            label: 'Les phases d\'atelier d\'un ordre, dans l\'ordre',
            help: 'Les vôtres, pas les nôtres : renommez, ajoutez, retirez. Les ordres déjà ouverts gardent les phases avec lesquelles ils sont nés.',
          },
          scrapPctDefault: {
            label: 'Chute supposée sur une nouvelle ligne de nomenclature',
            help: 'La perte de coupe et de manutention au-dessus de la quantité théorique. Chaque ligne peut dire la sienne ; ceci n\'est que le départ d\'une nouvelle.',
          },
          autoStartOnPhase: {
            label: 'Cocher la première phase démarre l\'ordre',
            help: 'Activé, l\'établi démarre un ordre en y travaillant. Désactivé, quelqu\'un avec le droit d\'écriture doit le démarrer d\'abord depuis le tableau.',
          },
        },
      },
    },
    audit: {
      actions: {
        production: {
          order_created: 'Ordre de fabrication ouvert',
          order_updated: 'Ordre modifié',
          order_pending: 'Ordre remis en attente',
          order_in_progress: 'Ordre mis en fabrication',
          order_on_hold: 'Ordre mis en pause',
          order_completed: 'Ordre terminé',
          order_cancelled: 'Ordre annulé',
          order_archived: 'Ordre archivé',
          order_restored: 'Ordre remis dans les listes',
          order_note_added: 'Note ajoutée à l\'ordre',
          phase_done: 'Phase d\'atelier cochée',
          phase_undone: 'Phase d\'atelier décochée',
          consumption_recorded: 'Consommation de matière notée',
          consumption_removed: 'Consommation de matière défaite',
          bom_created: 'Nomenclature créée',
          bom_updated: 'Nomenclature modifiée',
          bom_archived: 'Nomenclature archivée',
          bom_restored: 'Nomenclature remise dans les listes',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

export const messages = withToolLabels(withAnalytics(baseMessages, 'production'))

// Las tarjetas del asistente (production.assistant.*), en su propio fichero
// por la misma razón: son un contrato con backend/assistant.js.
import assistantMessages from './assistantMessages.js'

for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].production.assistant = { ...messages[locale].production.assistant, ...block }
}
